import React, { useState, useEffect, useCallback } from 'react'
import { Bell, BellOff, UserX, Target, Smartphone } from 'lucide-react'
import { fetchAnalytics } from '../../../../api/analyticsClient'
import KPICard from '../components/KPICard'
import AnalyticsCard from '../components/AnalyticsCard'
import styles from './RetentionSection.module.css'

const fmt = (n) => {
  if (n === null || n === undefined) return '—'
  const num = Number(n)
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K'
  return num.toLocaleString('en-IN')
}
const fmtPct = (n) => (n !== null && n !== undefined) ? parseFloat(n).toFixed(1) + '%' : '—'
const initState = () => ({ data: null, loading: true, error: null })

const BENCHMARKS = { d1: 40, d7: 20, d30: 10 }

const RetentionBadge = ({ rate, benchmark }) => {
  if (rate === null || rate === undefined) return null
  const pass = parseFloat(rate) >= benchmark
  return (
    <span className={`${styles.benchmark} ${pass ? styles.benchmarkPass : styles.benchmarkFail}`}>
      {pass ? 'Above' : 'Below'} benchmark ({benchmark}%)
    </span>
  )
}

const RetentionSection = ({ dateRange }) => {
  const [dayRetention, setDayRetention] = useState(initState)
  const [cohortDate, setCohortDate] = useState(() => {
    const d = new Date(Date.now() - 31 * 86400000)
    return d.toISOString().slice(0, 10)
  })
  const [cohortTable, setCohortTable] = useState(initState)
  const [churned, setChurned] = useState(initState)
  const [fcmReach, setFcmReach] = useState(initState)
  const [fcmPlatform, setFcmPlatform] = useState(initState)
  const [appVersion, setAppVersion] = useState(initState)

  const { from, to } = dateRange

  const loadAll = useCallback(async () => {
    setChurned(s => ({ ...s, loading: true, error: null }))
    setFcmReach(s => ({ ...s, loading: true, error: null }))
    setFcmPlatform(s => ({ ...s, loading: true, error: null }))
    setAppVersion(s => ({ ...s, loading: true, error: null }))
    setCohortTable(s => ({ ...s, loading: true, error: null }))

    const [churnedRes, fcmRes, fcmPlatRes, appVerRes, cohortRes] = await Promise.all([
      fetchAnalytics('/analytics/retention/churned-users'),
      fetchAnalytics('/analytics/retention/fcm-reachability'),
      fetchAnalytics('/analytics/retention/fcm-platform-split'),
      fetchAnalytics('/analytics/retention/app-version-distribution'),
      fetchAnalytics('/analytics/retention/cohort-table', { from, to }),
    ])

    setChurned({ data: churnedRes.data, loading: false, error: churnedRes.error })
    setFcmReach({ data: fcmRes.data, loading: false, error: fcmRes.error })
    setFcmPlatform({ data: fcmPlatRes.data, loading: false, error: fcmPlatRes.error })
    setAppVersion({ data: appVerRes.data, loading: false, error: appVerRes.error })
    setCohortTable({ data: cohortRes.data, loading: false, error: cohortRes.error })
  }, [from, to])

  const loadDayRetention = useCallback(async (date) => {
    setDayRetention({ data: null, loading: true, error: null })
    const res = await fetchAnalytics('/analytics/retention/day-retention', { cohort_date: date })
    setDayRetention({ data: res.data, loading: false, error: res.error })
  }, [])

  useEffect(() => { loadAll() }, [loadAll])
  useEffect(() => { loadDayRetention(cohortDate) }, [cohortDate, loadDayRetention])

  const fcmStatus = () => {
    const v = fcmReach.data?.reachability_pct
    if (v === null || v === undefined) return undefined
    if (v >= 60) return 'green'
    if (v >= 40) return 'amber'
    return 'red'
  }

  const getCellClass = (rate, benchmark) => {
    if (rate === null || rate === undefined) return ''
    return parseFloat(rate) >= benchmark ? styles.cohortGreen : styles.cohortRed
  }

  return (
    <div className={styles.section}>
      {/* Day Retention */}
      <div className={styles.retentionBlock}>
        <div className={styles.retentionHeader}>
          <div>
            <h3 className={styles.retentionTitle}>Day Retention</h3>
            <p className={styles.retentionSub}>D1/D7/D30 for a specific cohort date (MOBILE_APP registrations only)</p>
          </div>
          <div className={styles.cohortDateGroup}>
            <label className={styles.cohortDateLabel}>Cohort date</label>
            <input
              type="date"
              className={styles.cohortDateInput}
              value={cohortDate}
              max={new Date(Date.now() - 86400000).toISOString().slice(0, 10)}
              onChange={e => setCohortDate(e.target.value)}
            />
          </div>
        </div>

        {dayRetention.data && (
          <div className={styles.retentionNote}>
            Cohort size: <strong>{fmt(dayRetention.data.cohort_size)}</strong> stylists registered on {cohortDate}
          </div>
        )}

        <div className={styles.retentionKpis}>
          <AnalyticsCard title="D1 Retention" subtitle="Came back next day" loading={dayRetention.loading} error={dayRetention.error}>
            <div className={styles.retentionKpiContent}>
              <span className={styles.retentionRate}>{fmtPct(dayRetention.data?.d1?.rate_pct)}</span>
              <span className={styles.retentionRetained}>{fmt(dayRetention.data?.d1?.retained)} retained</span>
              <RetentionBadge rate={dayRetention.data?.d1?.rate_pct} benchmark={BENCHMARKS.d1} />
              <div className={styles.retentionBar}>
                <div className={styles.retentionBarFill} style={{ width: `${Math.min(100, dayRetention.data?.d1?.rate_pct || 0)}%`, background: (dayRetention.data?.d1?.rate_pct || 0) >= BENCHMARKS.d1 ? '#16a34a' : '#ef4444' }} />
                <div className={styles.retentionBenchmarkLine} style={{ left: `${BENCHMARKS.d1}%` }} title={`Benchmark: ${BENCHMARKS.d1}%`} />
              </div>
            </div>
          </AnalyticsCard>

          <AnalyticsCard title="D7 Retention" subtitle="Returned within 7 days" loading={dayRetention.loading} error={dayRetention.error}>
            <div className={styles.retentionKpiContent}>
              <span className={styles.retentionRate}>{fmtPct(dayRetention.data?.d7?.rate_pct)}</span>
              <span className={styles.retentionRetained}>{fmt(dayRetention.data?.d7?.retained)} retained</span>
              <RetentionBadge rate={dayRetention.data?.d7?.rate_pct} benchmark={BENCHMARKS.d7} />
              <div className={styles.retentionBar}>
                <div className={styles.retentionBarFill} style={{ width: `${Math.min(100, dayRetention.data?.d7?.rate_pct || 0)}%`, background: (dayRetention.data?.d7?.rate_pct || 0) >= BENCHMARKS.d7 ? '#16a34a' : '#ef4444' }} />
                <div className={styles.retentionBenchmarkLine} style={{ left: `${BENCHMARKS.d7}%` }} title={`Benchmark: ${BENCHMARKS.d7}%`} />
              </div>
            </div>
          </AnalyticsCard>

          <AnalyticsCard title="D30 Retention" subtitle="Returned within 30 days" loading={dayRetention.loading} error={dayRetention.error}>
            <div className={styles.retentionKpiContent}>
              <span className={styles.retentionRate}>{fmtPct(dayRetention.data?.d30?.rate_pct)}</span>
              <span className={styles.retentionRetained}>{fmt(dayRetention.data?.d30?.retained)} retained</span>
              <RetentionBadge rate={dayRetention.data?.d30?.rate_pct} benchmark={BENCHMARKS.d30} />
              <div className={styles.retentionBar}>
                <div className={styles.retentionBarFill} style={{ width: `${Math.min(100, dayRetention.data?.d30?.rate_pct || 0)}%`, background: (dayRetention.data?.d30?.rate_pct || 0) >= BENCHMARKS.d30 ? '#16a34a' : '#ef4444' }} />
                <div className={styles.retentionBenchmarkLine} style={{ left: `${BENCHMARKS.d30}%` }} title={`Benchmark: ${BENCHMARKS.d30}%`} />
              </div>
            </div>
          </AnalyticsCard>
        </div>
      </div>

      {/* Monthly Cohort Table */}
      <AnalyticsCard
        title="Monthly Cohort Retention"
        subtitle="Color-coded: green = above benchmark, red = below. This is the most expensive query."
        loading={cohortTable.loading}
        error={cohortTable.error}
        minHeight={120}
      >
        {cohortTable.data?.length > 0 && (
          <div className={styles.cohortTableWrap}>
            <table className={styles.cohortTable}>
              <thead>
                <tr>
                  <th className={styles.cohortTh}>Cohort</th>
                  <th className={styles.cohortTh}>Size</th>
                  <th className={styles.cohortTh}>D1 Retained</th>
                  <th className={styles.cohortTh}>D1 Rate</th>
                  <th className={styles.cohortTh}>D7 Retained</th>
                  <th className={styles.cohortTh}>D7 Rate</th>
                  <th className={styles.cohortTh}>D30 Retained</th>
                  <th className={styles.cohortTh}>D30 Rate</th>
                </tr>
              </thead>
              <tbody>
                {cohortTable.data.map((row) => (
                  <tr key={row.cohort_month} className={styles.cohortTr}>
                    <td className={`${styles.cohortTd} ${styles.cohortTdMonth}`}>{row.cohort_month}</td>
                    <td className={styles.cohortTd}>{fmt(row.cohort_size)}</td>
                    <td className={styles.cohortTd}>{fmt(row.d1_retained)}</td>
                    <td className={`${styles.cohortTd} ${getCellClass(row.d1_rate_pct, BENCHMARKS.d1)}`}>{fmtPct(row.d1_rate_pct)}</td>
                    <td className={styles.cohortTd}>{fmt(row.d7_retained)}</td>
                    <td className={`${styles.cohortTd} ${getCellClass(row.d7_rate_pct, BENCHMARKS.d7)}`}>{fmtPct(row.d7_rate_pct)}</td>
                    <td className={styles.cohortTd}>{fmt(row.d30_retained)}</td>
                    <td className={`${styles.cohortTd} ${getCellClass(row.d30_rate_pct, BENCHMARKS.d30)}`}>{fmtPct(row.d30_rate_pct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!cohortTable.loading && !cohortTable.error && cohortTable.data?.length === 0 && (
          <div className={styles.empty}>No cohort data for this range</div>
        )}
      </AnalyticsCard>

      {/* Churn + FCM Reachability */}
      <div className={styles.twoGrid}>
        <div className={styles.kpiRow}>
          <KPICard
            label="Churned Users"
            value={fmt(churned.data?.churned_users)}
            icon={UserX}
            status={churned.data?.churn_rate_pct > 30 ? 'red' : churned.data?.churn_rate_pct > 15 ? 'amber' : 'green'}
            subtext={churned.data ? `${fmtPct(churned.data.churn_rate_pct)} of ${fmt(churned.data.total_ever_active)} ever-active` : undefined}
            loading={churned.loading}
          />
          <KPICard
            label="FCM Reachability"
            value={fmtPct(fcmReach.data?.reachability_pct)}
            icon={Bell}
            status={fcmStatus()}
            subtext={fcmReach.data ? `${fmt(fcmReach.data.active_fcm_tokens)} of ${fmt(fcmReach.data.total_stylists)} stylists` : undefined}
            loading={fcmReach.loading}
          />
          <KPICard
            label="Unreachable Stylists"
            value={fmt(fcmReach.data?.unreachable_stylists)}
            icon={BellOff}
            status={fcmReach.data?.unreachable_stylists > 0 ? 'amber' : 'green'}
            subtext="Re-engagement opportunity"
            loading={fcmReach.loading}
          />
        </div>

        {/* FCM Reachability Progress */}
        {!fcmReach.loading && !fcmReach.error && fcmReach.data && (
          <AnalyticsCard title="FCM Reachability" subtitle="Target: >60% of stylists reachable via push notifications" loading={false} error={null}>
            <div className={styles.fcmContent}>
              <div className={styles.fcmRate}>{fmtPct(fcmReach.data.reachability_pct)}</div>
              <div className={styles.fcmProgressWrap}>
                <div className={styles.fcmProgressTrack}>
                  <div
                    className={styles.fcmProgressFill}
                    style={{
                      width: `${Math.min(100, fcmReach.data.reachability_pct || 0)}%`,
                      background: fcmStatus() === 'green' ? '#16a34a' : fcmStatus() === 'amber' ? '#f59e0b' : '#ef4444'
                    }}
                  />
                  <div className={styles.fcmTarget} title="Target: 60%" />
                </div>
                <div className={styles.fcmProgressLabels}>
                  <span>0%</span>
                  <span className={styles.fcmTargetLabel}>60% target</span>
                  <span>100%</span>
                </div>
              </div>
              <div className={styles.fcmMeta}>
                <span>{fmt(fcmReach.data.active_fcm_tokens)} active tokens</span>
                <span className={styles.fcmDot} />
                <span>{fmt(fcmReach.data.unreachable_stylists)} unreachable</span>
              </div>
            </div>
          </AnalyticsCard>
        )}
      </div>

      {/* FCM Platform Split */}
      <AnalyticsCard
        title="FCM Platform Split"
        subtitle="Token health by platform and deactivation reasons"
        loading={fcmPlatform.loading}
        error={fcmPlatform.error}
      >
        {fcmPlatform.data && (
          <div className={styles.fcmPlatformContent}>
            {fcmPlatform.data.by_platform?.length > 0 && (
              <div className={styles.fcmPlatformTable}>
                <div className={styles.fcmTableHeader}>Token Status by Platform</div>
                <table className={styles.simpleTable}>
                  <thead>
                    <tr>
                      <th className={styles.simpleTh}>Platform</th>
                      <th className={styles.simpleTh}>Total Tokens</th>
                      <th className={styles.simpleTh}>Active</th>
                      <th className={styles.simpleTh}>Inactive</th>
                      <th className={styles.simpleTh}>Avg Failures</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fcmPlatform.data.by_platform.map((p) => (
                      <tr key={p.platform} className={styles.simpleTr}>
                        <td className={styles.simpleTd}>{p.platform}</td>
                        <td className={styles.simpleTd}>{fmt(p.total_tokens)}</td>
                        <td className={`${styles.simpleTd} ${styles.colorGreen}`}>{fmt(p.active_tokens)}</td>
                        <td className={`${styles.simpleTd} ${styles.colorRed}`}>{fmt(p.inactive_tokens)}</td>
                        <td className={styles.simpleTd}>{Number(p.avg_failures || 0).toFixed(1)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {fcmPlatform.data.deactivation_reasons?.length > 0 && (
              <div className={styles.fcmPlatformTable}>
                <div className={styles.fcmTableHeader}>Deactivation Reasons</div>
                <table className={styles.simpleTable}>
                  <thead>
                    <tr>
                      <th className={styles.simpleTh}>Platform</th>
                      <th className={styles.simpleTh}>Reason</th>
                      <th className={styles.simpleTh}>Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fcmPlatform.data.deactivation_reasons.map((r, i) => (
                      <tr key={i} className={styles.simpleTr}>
                        <td className={styles.simpleTd}>{r.platform}</td>
                        <td className={styles.simpleTd}>{r.deactivation_reason || 'Unknown'}</td>
                        <td className={styles.simpleTd}>{fmt(r.count)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </AnalyticsCard>

      {/* App Version Distribution */}
      <AnalyticsCard
        title="App Version Distribution"
        subtitle="Track force-update rollout and identify outdated version clusters"
        loading={appVersion.loading}
        error={appVersion.error}
      >
        {appVersion.data?.length > 0 && (
          <div className={styles.tableWrap}>
            <table className={styles.simpleTable}>
              <thead>
                <tr>
                  <th className={styles.simpleTh}>Version</th>
                  <th className={styles.simpleTh}>Platform</th>
                  <th className={styles.simpleTh}>Total Tokens</th>
                  <th className={styles.simpleTh}>Active Tokens</th>
                  <th className={styles.simpleTh}>Active Rate</th>
                </tr>
              </thead>
              <tbody>
                {appVersion.data.map((row, i) => {
                  const activeRate = row.token_count > 0 ? (row.active_count / row.token_count) * 100 : 0
                  return (
                    <tr key={i} className={styles.simpleTr}>
                      <td className={`${styles.simpleTd} ${styles.versionCell}`}>{row.app_version}</td>
                      <td className={styles.simpleTd}>{row.platform}</td>
                      <td className={styles.simpleTd}>{fmt(row.token_count)}</td>
                      <td className={styles.simpleTd}>{fmt(row.active_count)}</td>
                      <td className={`${styles.simpleTd} ${activeRate > 80 ? styles.colorGreen : activeRate > 50 ? styles.colorAmber : styles.colorRed}`}>
                        {fmtPct(activeRate)}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </AnalyticsCard>
    </div>
  )
}

export default RetentionSection
